/**
 * carousel-manager.js
 * -----------------------------------------------------------------------------
 * Decides WHEN the Flickity vendor should load, so carousels no longer force the
 * 180KB vendor blob onto the critical path.
 *
 * Carousels sit below the fold on every page type (product, collection, page,
 * article) and even the homepage hero shows its first slide via CSS before
 * Flickity initializes (see `blocks/slideshow.liquid`). So we defer loading
 * Flickity until a carousel approaches the viewport, using IntersectionObserver.
 *
 * Ownership of `new Flickity(...)` stays in theme.js / custom.js (the Carousel
 * class). Those call `theme.whenFlickityReadyFor(element)` and only build the
 * instance once the returned Promise resolves.
 *
 * Public API (attached to `window.theme`):
 *   theme.whenFlickityReadyFor(element) -> Promise  // resolves when Flickity is loaded
 *   theme.flickityReady                 -> Promise  // resolves once, when Flickity is loaded
 */
(function () {
  'use strict';

  window.theme = window.theme || {};

  // How far outside the viewport a carousel can be before we start loading.
  // 600px gives the vendor time to download/parse so there is no visible pop-in.
  var ROOT_MARGIN = '600px 0px';

  var observer = null;
  var pendingResolvers = []; // resolvers for whenFlickityReadyFor() calls
  var loadStarted = false;

  var resolveFlickityReady;
  window.theme.flickityReady = new Promise(function (resolve) {
    resolveFlickityReady = resolve;
  });

  function flickityAvailable() {
    return typeof window.Flickity !== 'undefined';
  }

  function startLoad() {
    if (loadStarted) {
      return;
    }
    loadStarted = true;

    if (observer) {
      observer.disconnect();
      observer = null;
    }

    var load = (window.theme && window.theme.loadFlickity)
      ? window.theme.loadFlickity()
      : Promise.resolve();

    load.then(function () {
      resolveFlickityReady();
      var resolvers = pendingResolvers;
      pendingResolvers = [];
      resolvers.forEach(function (resolve) { resolve(); });
    });
  }

  function getObserver() {
    if (observer) {
      return observer;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return null;
    }
    observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          startLoad();
          break;
        }
      }
    }, { rootMargin: ROOT_MARGIN });
    return observer;
  }

  window.theme.whenFlickityReadyFor = function (element) {
    if (flickityAvailable()) {
      return Promise.resolve();
    }

    return new Promise(function (resolve) {
      pendingResolvers.push(resolve);

      // Without an element to observe, or without IntersectionObserver support,
      // fall back to loading immediately (matches the previous eager behavior).
      var io = element ? getObserver() : null;
      if (io) {
        io.observe(element);
      } else {
        startLoad();
      }
    });
  };
})();
